// Core imports
import { Fragment, useState, useRef, useCallback } from 'react';
import { t } from 'i18next';
import PropTypes from 'prop-types';

// Component imports
import { Typography, Tooltip } from 'antd';

// Misc imports
import useSharedObserver from '../../utils/hooks/useObserverSingleton';

/**
 * Truncates text with a toggleable "Show more" / "Show less" button.
 * @param {Object} props
 * @param {string} props.text - The text content to clamp.
 * @param {number} [props.linesToClamp=3] - The maximum number of lines to display.
 * @param {boolean} [props.showMore=true] - Whether to show the toggle button if the text overflows.
 * @param {boolean} [props.enableTooltip=false] - Whether to wrap the text in a tooltip showing the full text.
 * NOTE: Only supports values between 1 and 10 unless new `line-clamp-X` classes are added to the CSS.
 */
const TruncatedTextWithShowMore = ({
  text,
  linesToClamp = 3,
  showMore = true,
  enableTooltip = false
}) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const sentinelRef = useRef(null);

  const handleSentinelChange = useCallback((entry) => {
    if (!expanded) {
      setIsOverflowing(!entry.isIntersecting);
    }
  }, [expanded]);

  useSharedObserver(sentinelRef, handleSentinelChange);

  if (!text) {
    return null;
  }

  const safeLinesToClamp = Math.max(1, Math.min(10, linesToClamp));

  const textNode = <Typography.Text>{text}</Typography.Text>;

  const displayNode = enableTooltip ? (
    <Tooltip title={text} placement="top" mouseEnterDelay={0.4}>
      {textNode}
    </Tooltip>
  ) : (
    textNode
  );

  return (
    <Fragment>
      <div
        className={`word-break-break-all position-relative ${!expanded ? `line-clamp-${safeLinesToClamp}` : ''}`}
      >
        {displayNode}
        <span
          ref={sentinelRef}
          aria-hidden="true"
          className="pointer-events-none bg-transparent d-inline-block sentinel-element"
        />
      </div>

      {showMore && (isOverflowing || expanded) && (
        <div className="mt-1">
          <Typography.Link onClick={() => setExpanded(!expanded)}>
            {expanded ? t('Show less') : t('Show more')}
          </Typography.Link>
        </div>
      )}
    </Fragment>
  );
};

TruncatedTextWithShowMore.propTypes = {
  text: PropTypes.string,
  showMore: PropTypes.bool,
  enableTooltip: PropTypes.bool,
  linesToClamp: function(props, propName, componentName) {
    const value = props[propName];
    if (value !== undefined) {
      if (typeof value !== 'number' || value < 1 || value > 10) {
        return new Error(
          `Invalid prop \`${propName}\` of value \`${value}\` supplied to \`${componentName}\`. `
          + `Expected a number between 1 and 10 unless additional \`line-clamp-X\` classes are added to the CSS.`
        );
      }
    }
    return null;
  }
};

export default TruncatedTextWithShowMore;
